import { Dialog as SheetPrimitive } from 'radix-ui';
import type * as React from 'react';
import { createContext, useContext } from 'react';
import { LuX } from 'react-icons/lu';

import { cn } from '~utils/cn';
import { Button } from './button';

type SheetSide = 'inline-start' | 'inline-end' | 'block-start' | 'block-end';

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & {
  portalContainer?: React.ComponentProps<typeof SheetPrimitive.Portal>['container'];
  side?: SheetSide;
  showCloseButton?: boolean;
  showOverlay?: boolean;
  strategy?: 'fixed' | 'absolute';
};

type SheetHeaderProps = React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
};

const SheetCloseButtonContext = createContext(true);

const SHEET_SIDE_STYLES: Record<SheetSide, string> = {
  'inline-end':
    'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 end-0 h-full w-(--sheet-width) border-s',
  'inline-start':
    'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 start-0 h-full w-(--sheet-width) border-e',
  'block-start':
    'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top [inset-inline:0] [inset-block-start:0] h-auto border-b',
  'block-end':
    'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom [inset-inline:0] [inset-block-end:0] h-auto border-t',
};

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ asChild, children, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  if (asChild || children) {
    return (
      <SheetPrimitive.Close data-slot="sheet-close" asChild={asChild} className={className} {...props}>
        {children}
      </SheetPrimitive.Close>
    );
  }

  return (
    <SheetPrimitive.Close data-slot="sheet-close" asChild {...props}>
      <Button
        icon
        size="sm"
        variant="ghost"
        type="button"
        aria-label="Close details sheet"
        className={cn('ms-auto', className)}
      >
        <LuX className="size-6" aria-hidden="true" />
      </Button>
    </SheetPrimitive.Close>
  );
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-veil data-[state=closed]:animate-out data-[state=open]:animate-in',
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  portalContainer,
  side = 'inline-end',
  showCloseButton = true,
  showOverlay = true,
  strategy = 'fixed',
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal container={portalContainer}>
      {showOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'z-50 flex flex-col gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:duration-200 data-[state=open]:duration-200',
          strategy,
          SHEET_SIDE_STYLES[side],
          className,
        )}
        {...props}
      >
        <SheetCloseButtonContext.Provider value={showCloseButton}>{children}</SheetCloseButtonContext.Provider>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, children, showCloseButton, ...props }: SheetHeaderProps) {
  const showContentCloseButton = useContext(SheetCloseButtonContext);
  const shouldShowCloseButton = showCloseButton ?? showContentCloseButton;

  return (
    <div data-slot="sheet-header" className={cn('flex justify-between bg-muted px-6 py-5', className)} {...props}>
      {children}
      {shouldShowCloseButton && <SheetClose />}
    </div>
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-bold text-xl leading-normal', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
